import { Link } from 'react-router-dom'

const DISCORD_URL =
  import.meta.env.VITE_DISCORD_URL || 'https://discord.gg/R4udP6TbM2'

type FooterLink =
  | { label: string; to: string }
  | { label: string; href: string; external?: boolean }

type SocialIconName = 'x' | 'instagram' | 'discord' | 'github' | 'linkedin'

interface SocialLink {
  label: string
  href: string
  icon: SocialIconName
}

const FOOTER_GROUPS: Array<{ title: string; links: FooterLink[] }> = [
  {
    title: 'Product',
    links: [
      { label: 'How it works', href: '/#how' },
      { label: 'What is opendeploy?', to: '/what-is-opendeploy' },
      { label: 'Pricing', to: '/pricing' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Docs', to: '/docs' },
      { label: 'Blog', to: '/blog' },
      { label: 'Events', to: '/events' },
      { label: 'Templates', to: '/templates' },
      { label: 'Change logs', to: '/changelog' },
      { label: 'FAQ', to: '/why' },
      { label: 'Dashboard', href: 'https://dashboard.opendeploy.dev/', external: true },
    ],
  },
  {
    title: 'Agent',
    links: [
      { label: 'Start page', href: '/start' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms of Service', to: '/terms' },
      { label: 'Privacy Policy', to: '/privacy' },
    ],
  },
]

const SOCIAL_LINKS: SocialLink[] = [
  { label: 'X / Twitter', href: 'https://x.com/opendeploy', icon: 'x' },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/opendeploy/',
    icon: 'instagram',
  },
  { label: 'GitHub', href: 'https://github.com/opendeploy-dev', icon: 'github' },
  { label: 'LinkedIn', href: 'https://linkedin.com/company/opendeploy/', icon: 'linkedin' },
  { label: 'Discord', href: DISCORD_URL, icon: 'discord' },
]

const FOLLOW_LINKS = SOCIAL_LINKS.filter((link) => link.icon !== 'discord')

function FooterLogo() {
  return (
    <span className="od-footer-logo" aria-hidden="true">
      <svg viewBox="0 0 180 90" focusable="false">
        <circle cx="44" cy="45" r="24" stroke="currentColor" strokeWidth="14" fill="none" />
        <path d="M86 68L112 22" stroke="currentColor" strokeWidth="14" strokeLinecap="square" />
      </svg>
    </span>
  )
}

function FooterLinkItem({ link }: { link: FooterLink }) {
  if ('to' in link) {
    return (
      <Link className="od-footer-link" to={link.to}>
        {link.label}
      </Link>
    )
  }

  return (
    <a
      className="od-footer-link"
      href={link.href}
      target={link.external ? '_blank' : undefined}
      rel={link.external ? 'noopener noreferrer' : undefined}
    >
      {link.label}
    </a>
  )
}

function SocialIcon({ name }: { name: SocialIconName }) {
  if (name === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
      </svg>
    )
  }

  if (name === 'discord') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"
          fill="currentColor"
          stroke="none"
        />
      </svg>
    )
  }

  if (name === 'github') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M12 2C6.48 2 2 6.59 2 12.25c0 4.52 2.86 8.35 6.84 9.7.5.09.68-.22.68-.49v-1.9c-2.78.62-3.37-1.22-3.37-1.22-.45-1.18-1.11-1.49-1.11-1.49-.91-.64.07-.63.07-.63 1 .07 1.53 1.06 1.53 1.06.89 1.56 2.34 1.11 2.91.85.09-.66.35-1.11.63-1.36-2.22-.26-4.56-1.14-4.56-5.06 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.3.1-2.71 0 0 .84-.28 2.75 1.05A9.37 9.37 0 0 1 12 6.96c.85 0 1.7.12 2.5.34 1.91-1.33 2.75-1.05 2.75-1.05.55 1.41.2 2.45.1 2.71.64.72 1.03 1.63 1.03 2.75 0 3.93-2.34 4.8-4.57 5.06.36.32.68.94.68 1.9v2.8c0 .27.18.59.69.49A10.14 10.14 0 0 0 22 12.25C22 6.59 17.52 2 12 2Z"
          fill="currentColor"
          stroke="none"
        />
      </svg>
    )
  }

  if (name === 'linkedin') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M5.34 8.84H2.67v12.02h2.67V8.84ZM4 3.14a1.56 1.56 0 1 0 0 3.12 1.56 1.56 0 0 0 0-3.12Zm6.32 5.7H7.77v12.02h2.67v-6.32c0-1.69.78-3.03 2.45-3.03 1.35 0 2 .91 2 2.66v6.69h2.67v-7.22c0-3.17-1.55-5.06-4.19-5.06-1.46 0-2.41.64-3.05 1.58V8.84Z"
          fill="currentColor"
          stroke="none"
        />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M18.2 3h3.1l-6.8 7.8 8 10.5h-6.2l-4.9-6.4-5.6 6.4H2.7l7.3-8.3L2.3 3h6.4l4.4 5.8L18.2 3Zm-1.1 16.4h1.7L7.8 4.8H6.1l11 14.6Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  )
}

export default function Footer() {
  return (
    <footer className="od-footer" aria-label="Footer">
      <div className="od-footer-inner">
      <div className="od-footer-brand">
        <Link to="/" className="od-footer-brand-link" aria-label="opendeploy home">
          <FooterLogo />
          <span>opendeploy</span>
        </Link>
        <p>The place gen-apps live.</p>
        <a className="od-footer-email" href="mailto:hi@opendeploy.dev">
          hi@opendeploy.dev
        </a>
      </div>

      <div className="od-footer-links" aria-label="Footer navigation">
        {FOOTER_GROUPS.map((group) => (
          <section key={group.title} className="od-footer-group">
            <h2>{group.title}</h2>
            <div>
              {group.links.map((link) => (
                <FooterLinkItem key={link.label} link={link} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <aside className="od-footer-community" aria-label="Community links">
        <section className="od-footer-community-section">
          <h2>Join our Discord</h2>
          <a
            className="od-footer-discord-link"
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <SocialIcon name="discord" />
            <span>Join Discord</span>
          </a>
        </section>

        <section className="od-footer-community-section">
          <h2>Follow us on social media</h2>
          <div className="od-footer-social-icons" aria-label="Social links">
            {FOLLOW_LINKS.map((link) => (
              <a
                key={link.label}
                className="od-footer-social-link"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.label}
                title={link.label}
              >
                <SocialIcon name={link.icon} />
              </a>
            ))}
          </div>
        </section>
      </aside>

      <div className="od-footer-bottom">
        <p>&copy; 2026 opendeploy.dev. All rights reserved.</p>
      </div>
      </div>
    </footer>
  )
}
